import { ApiError } from '../core/api/http'
import { nowStamp } from '../core/format/dates'
import { customerName } from '../admin/customers/customerRules'
import { db } from './db'
import { demoId, reply } from './reply'
import { demoAdminIdentity } from './adminIdentity'
import type { AdminBackend } from '../core/types/backend'
import type { HandoffNote } from '../core/types/handoff'

/**
 * DEMO — overdrachtsnotities van de collega aan Sarah. Bewaard in localStorage, zodat ze blijven
 * staan als je uitlogt als Mila en inlogt als Sarah. De server bewaart ze in `handoff_notes`.
 */
const KEY = 'bb-demo-handoff-notes'
const MAX_LENGTH = 1000

type Row = HandoffNote & { requestId: string }

const load = (): Row[] => {
  try {
    const raw = localStorage.getItem(KEY)
    const list: unknown = raw ? JSON.parse(raw) : []
    return Array.isArray(list) ? (list as Row[]) : []
  } catch {
    return []
  }
}
const save = (rows: Row[]) => {
  try {
    localStorage.setItem(KEY, JSON.stringify(rows))
  } catch {
    /* niet erg in de demo */
  }
}
const strip = ({ requestId: _r, ...n }: Row): HandoffNote => n
const ownerOnly = () => {
  if (demoAdminIdentity.access().grants['customers.edit'] !== 'all') throw new ApiError('Dit deel is alleen voor Sarah.', 403, undefined, 'forbidden')
}

/** Bij samenvoegen: de overdrachtsnotities van het opgeheven dossier horen nu bij het andere. */
export function repointHandoffNotes(fromId: string, toId: string, toName: string) {
  const rows = load()
  if (rows.some((n) => n.customerId === fromId)) save(rows.map((n) => (n.customerId === fromId ? { ...n, customerId: toId, customerName: toName } : n)))
}

/** Account verwijderd: overdrachtsnotities over deze klant gaan mee weg (geen bewaarplicht). */
export function removeHandoffNotesFor(customerId: string) {
  const rows = load()
  if (rows.some((n) => n.customerId === customerId)) save(rows.filter((n) => n.customerId !== customerId))
}

export const demoHandoffBackend: Pick<AdminBackend, 'fetchHandoffNotes' | 'addHandoffNote' | 'markHandoffNoteRead'> = {
  async fetchHandoffNotes() {
    ownerOnly()
    const list = load()
      .filter((n) => !n.readAt)
      .sort((a, b) => b.createdAt.localeCompare(a.createdAt))
      .map(strip)
    return reply(list, 200)
  },

  async addHandoffNote(customerId, { requestId, text, appointmentId }) {
    const rows = load()
    const seen = rows.find((n) => n.requestId === requestId)
    if (seen) return reply(strip(seen), 200)
    const clean = text.trim()
    if (!clean) throw new ApiError('Schrijf eerst iets.', 422, { text: 'Schrijf eerst iets.' })
    if (clean.length > MAX_LENGTH) throw new ApiError(`Hooguit ${MAX_LENGTH} tekens.`, 422, { text: `Hooguit ${MAX_LENGTH} tekens.` })
    const account = db.account(customerId)
    if (!account) throw new ApiError('Deze klant bestaat niet (meer).', 404)
    const me = demoAdminIdentity.current()
    // Een collega alleen bij een klant met een afspraak bij haar (zoals de klantkaart).
    const visits = db.appointments().filter((a) => a.customerId === customerId)
    if (demoAdminIdentity.access().grants['customers.edit'] !== 'all' && !visits.some((a) => demoAdminIdentity.can('customers.edit', a))) {
      throw new ApiError('Deze klant heeft geen afspraak bij jou.', 403, undefined, 'not_your_customer')
    }
    const row: Row = {
      id: demoId('ov').toLowerCase(),
      requestId,
      customerId,
      customerName: customerName(account.customer),
      ...(appointmentId ? { appointmentId } : {}),
      fromStaffId: me.staffId,
      fromName: me.name,
      accountId: me.accountId,
      text: clean,
      createdAt: nowStamp(),
    }
    save([row, ...rows])
    return reply(strip(row), 300)
  },

  async markHandoffNoteRead(id) {
    ownerOnly()
    const rows = load()
    const row = rows.find((n) => n.id === id)
    if (!row) throw new ApiError('Deze notitie bestaat niet meer.', 404)
    const updated = { ...row, readAt: row.readAt ?? nowStamp() }
    save(rows.map((n) => (n.id === id ? updated : n)))
    return reply(strip(updated), 200)
  },
}
